import { Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Reusable per-row trash icon button.
 * Shows a confirm dialog, calls DELETE on the provided endpoint, then calls onDeleted.
 *
 * Props:
 *  - endpoint: string — full API path WITHOUT leading /api, e.g. "/leads/abc-123"
 *  - confirmMessage: string — message inside window.confirm
 *  - onDeleted: () => void — invoked after a successful delete
 *  - testid: string — unique data-testid for the button
 *  - successMessage: optional — defaults to "Deleted"
 *  - cascade: optional — if true, show cascade summary in toast.success
 */
export default function RowDeleteButton({ endpoint, confirmMessage, onDeleted, testid, successMessage = "Deleted", cascade = false }) {
  const handle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm(confirmMessage)) return;
    try {
      const res = await api.delete(endpoint);
      if (cascade && res?.data?.cascade) {
        const parts = Object.entries(res.data.cascade)
          .filter(([, n]) => n > 0)
          .map(([k, n]) => `${n} ${k.replace(/_/g, " ")}`)
          .join(", ");
        toast.success(parts ? `${successMessage} (cascaded: ${parts})` : successMessage);
      } else {
        toast.success(successMessage);
      }
      onDeleted?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Failed to delete";
      toast.error(msg);
    }
  };

  return (
    <button
      type="button"
      onClick={handle}
      title="Delete"
      className="inline-flex items-center justify-center w-7 h-7 text-[#9CA3AF] hover:text-[#DC2626] hover:bg-[#FEF2F2] border border-transparent hover:border-[#FECACA] transition-colors"
      data-testid={testid}
    >
      <Trash2 className="w-3.5 h-3.5" />
    </button>
  );
}
